import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import { EmptyState, ListRow, SectionHeader, StatusPill } from '../components/PlayerUI';
import SupportChat from '../components/SupportChat';
import { SkeletonCard } from '../components/Skeleton';
import { useAlert } from '../hooks/useAlert';
import { useTheme } from '../context/ThemeContext';

const BALANCE_VISIBILITY_KEY = 'sls_balance_hidden';

interface UserProfile { fullName: string }
interface WalletSummary { balance: number }
interface TopUpOrder {
  id: string;
  amount: number | string;
  status: string;
  createdAt: string;
  target?: 'game' | 'wallet';
  paymentSource?: 'method' | 'wallet';
  game?: { name: string } | null;
  paymentMethod?: { name: string } | null;
}
interface WithdrawOrder {
  id: string;
  amount: number | string;
  status: string;
  createdAt: string;
  source?: 'game' | 'wallet';
  paymentMethod?: { name: string } | null;
  payoutTag?: string;
}
type Activity = {
  id: string;
  type: 'topup' | 'withdraw';
  amount: number;
  direction: '+' | '−';
  counterparty: string;
  description: string;
  status: string;
  createdAt: string;
};

function mappedStatus(status: string): 'processing' | 'completed' | 'rejected' {
  if (['Completed', 'Paid', 'Verified'].includes(status)) return 'completed';
  if (['Rejected', 'Cancelled'].includes(status)) return 'rejected';
  return 'processing';
}

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { alert } = useAlert();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [topUps, setTopUps] = useState<TopUpOrder[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [hideBalance, setHideBalance] = useState(() => {
    try { return localStorage.getItem(BALANCE_VISIBILITY_KEY) === '1'; } catch { return false; }
  });
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [referral, setReferral] = useState<{ code: string; rewardAmount: string } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [profileResult, walletResult, topUpResult, withdrawalResult] = await Promise.allSettled([
      api.get<UserProfile>('/auth/me'),
      api.get<WalletSummary>('/wallet/me'),
      api.get<TopUpOrder[]>('/orders/topup/my'),
      api.get<WithdrawOrder[]>('/orders/withdraw/my'),
    ]);
    if (profileResult.status === 'fulfilled') setUser(profileResult.value.data);
    if (walletResult.status === 'fulfilled') setWallet(walletResult.value.data);
    if (topUpResult.status === 'fulfilled') setTopUps(topUpResult.value.data);
    if (withdrawalResult.status === 'fulfilled') setWithdrawals(withdrawalResult.value.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    try { localStorage.setItem(BALANCE_VISIBILITY_KEY, hideBalance ? '1' : '0'); } catch { /* Storage may be unavailable. */ }
  }, [hideBalance]);

  const activities = useMemo<Activity[]>(() => {
    const loads = topUps.map(order => ({
      id: order.id,
      type: 'topup' as const,
      amount: Number(order.amount) || 0,
      direction: order.target === 'wallet' ? '+' as const : '−' as const,
      counterparty: order.target === 'wallet'
        ? order.paymentMethod?.name || t('home.screen.slSweep')
        : order.game?.name || t('home.screen.game'),
      description: order.target === 'wallet' ? t('home.screen.addedToBalance') : t('home.screen.gameLoad'),
      status: order.status,
      createdAt: order.createdAt,
    }));
    const cashOuts = withdrawals.map(order => ({
      id: order.id,
      type: 'withdraw' as const,
      amount: Number(order.amount) || 0,
      direction: '−' as const,
      counterparty: order.paymentMethod?.name || order.payoutTag || t('home.screen.cashOut'),
      description: order.source === 'game' ? t('home.screen.gameCashOut') : t('home.screen.balanceCashOut'),
      status: order.status,
      createdAt: order.createdAt,
    }));
    return [...loads, ...cashOuts]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, [topUps, withdrawals, t]);

  const submitPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoSubmitting(true);
    try {
      const response = await api.post('/promo/redeem', { code });
      setPromoOpen(false);
      setPromoCode('');
      alert(t('home.promo.successTitle'), t('home.promo.successMessage', { amount: response.data.amount, newBalance: response.data.newBalance.toFixed(2) }));
      fetchData();
    } catch (error: any) {
      alert(t('home.promo.failedTitle'), String(error?.response?.data?.message || t('home.promo.failedDefault')));
    } finally {
      setPromoSubmitting(false);
    }
  };

  const openReferral = async () => {
    setReferralOpen(true);
    if (referral) return;
    try {
      const response = await api.get('/me/referral');
      setReferral(response.data);
    } catch {
      setReferral(null);
    }
  };

  const shareReferral = async () => {
    if (!referral?.code) return;
    const url = `${window.location.origin}/register?ref=${referral.code}`;
    const text = t('home.referral.shareText', { code: referral.code, url });
    if (navigator.share) await navigator.share({ title: t('home.referral.shareTitle'), text });
    else await navigator.clipboard.writeText(text);
  };

  return (
    <main className="sls-page">
      <div className="sls-page-content" style={{ paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))' }}>
        {loading ? (
          <><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
        ) : (
          <>
            <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <img src="/icons/icon-192.png" alt="SL Sweep" style={{ width: 44, height: 44, objectFit: 'contain' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button aria-label={t('home.screen.openStreak')} onClick={() => navigate('/events')} style={{ width: 48, height: 48, border: 0, borderRadius: 24, background: colors.line, display: 'grid', placeItems: 'center' }}>
                  <Icon name="calendar-check" size={19} color={colors.text} />
                </button>
                <button aria-label={t('home.screen.openNotifications')} onClick={() => navigate('/notifications')} style={{ width: 48, height: 48, border: 0, borderRadius: 24, background: colors.line, display: 'grid', placeItems: 'center' }}>
                  <Icon name="bell" size={19} color={colors.text} />
                </button>
              </div>
            </header>

            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: colors.text }}>
              {t('home.screen.greeting', { name: user?.fullName?.split(' ')[0] || t('home.screen.greetingFallback') })}
            </h1>

            <section aria-labelledby="balance-heading" style={{ marginTop: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 id="balance-heading" style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.text }}>{t('home.screen.balance')}</h2>
                <button aria-label={hideBalance ? t('home.screen.showBalance') : t('home.screen.hideBalance')} onClick={() => setHideBalance(value => !value)} style={{ width: 48, height: 48, border: 0, background: 'transparent', display: 'grid', placeItems: 'center' }}>
                  <Icon name={hideBalance ? 'eye-slash' : 'eye'} size={18} color={colors.textMuted} />
                </button>
              </div>
              <div className="sls-tnum" style={{ marginTop: 2, fontSize: 48, lineHeight: 1.1, fontWeight: 700, color: colors.text }}>
                {hideBalance ? '••••' : `$${(wallet?.balance || 0).toFixed(2)}`}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 24 }}>
                <Button label={t('home.screen.addFunds')} icon="plus" onClick={() => navigate('/wallet-topup')} />
                <Button label={t('home.screen.cashOut')} variant="secondary" onClick={() => navigate('/withdraw?mode=wallet')} />
              </div>
            </section>

            <SectionHeader title={t('home.screen.actionsTitle')} />
            <div>
              <ListRow icon="gamepad" label={t('home.screen.loadGame')} onPress={() => navigate('/topup')} />
              <ListRow icon="tag" label={t('home.screen.promoCode')} onPress={() => setPromoOpen(true)} />
              <ListRow icon="gift" label={t('home.screen.inviteFriends')} onPress={openReferral} />
            </div>

            <SectionHeader title={t('home.screen.recentActivity')} />
            {activities.length ? activities.map(activity => (
              <button key={`${activity.type}-${activity.id}`} onClick={() => navigate(`/order/${activity.type}/${activity.id}`)} style={{ width: '100%', minHeight: 76, padding: '10px 0', display: 'flex', alignItems: 'center', gap: 12, border: 0, borderBottom: `1px solid ${colors.line}`, background: 'transparent', textAlign: 'left' }}>
                <span className="sls-tnum" style={{ minWidth: 82, fontSize: 17, fontWeight: 700, color: colors.text }}>{activity.direction}${activity.amount.toFixed(2)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activity.counterparty}</span>
                  <span style={{ display: 'block', marginTop: 3, fontSize: 14, color: colors.textMuted }}>{activity.description}</span>
                </span>
                <StatusPill status={mappedStatus(activity.status)} label={t(`status.${activity.status}`, activity.status)} />
              </button>
            )) : (
              <EmptyState icon="receipt" title={t('home.screen.noTransactions')} body={t('home.screen.noTransactionsBody')} action={{ label: t('home.screen.addFunds'), onPress: () => navigate('/wallet-topup') }} />
            )}
          </>
        )}
      </div>

      {/* Floating support chat launcher */}
      <button
        aria-label={t('home.supportFab')}
        onClick={() => setChatOpen(true)}
        style={{
          position: 'fixed', right: 'max(18px, calc(50% - 222px))',
          bottom: 'calc(70px + 24px + env(safe-area-inset-bottom, 0px))',
          width: 56, height: 56, borderRadius: 28,
          backgroundColor: colors.accent, color: '#000000',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 22px rgba(0, 0, 0, 0.35)',
          zIndex: 90,
        }}
      >
        <Icon name="headset" size={20} color="#000000" />
      </button>
      <SupportChat open={chatOpen} onClose={() => setChatOpen(false)} />

      <Modal open={promoOpen} onClose={() => setPromoOpen(false)}>
        <div style={{ fontSize: 20, fontWeight: 700, color: colors.text }}>{t('home.screen.promoTitle')}</div>
        <p style={{ fontSize: 16, lineHeight: 1.5, color: colors.textSub }}>{t('home.screen.promoBody')}</p>
        <label style={{ display: 'block', fontSize: 16, fontWeight: 600, color: colors.text }}>
          {t('home.screen.promoLabel')}
          <input value={promoCode} onChange={event => setPromoCode(event.target.value)} autoCapitalize="characters" style={{ width: '100%', minHeight: 56, marginTop: 8, padding: '0 16px', border: `1px solid ${colors.line}`, borderRadius: 12, background: colors.line, color: colors.text, fontSize: 17 }} />
        </label>
        <Button label={t('home.screen.applyPromo')} loading={promoSubmitting} disabled={!promoCode.trim()} onClick={submitPromo} style={{ marginTop: 20 }} />
        <Button label={t('actions.cancel')} variant="secondary" onClick={() => setPromoOpen(false)} style={{ marginTop: 10 }} />
      </Modal>

      <Modal open={referralOpen} onClose={() => setReferralOpen(false)}>
        <div style={{ fontSize: 20, fontWeight: 700, color: colors.text }}>{t('home.screen.inviteTitle')}</div>
        <p style={{ fontSize: 16, lineHeight: 1.5, color: colors.textSub }}>{t('home.screen.inviteBody')}</p>
        {referral ? (
          <>
            <div style={{ padding: 16, borderRadius: 12, background: colors.line, textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: 2 }}>{referral.code}</div>
            <Button label={t('home.screen.shareInvite')} onClick={shareReferral} style={{ marginTop: 20 }} />
          </>
        ) : <div style={{ fontSize: 16, color: colors.textSub }}>{t('home.screen.inviteUnavailable')}</div>}
        <Button label={t('actions.close')} variant="secondary" onClick={() => setReferralOpen(false)} style={{ marginTop: 10 }} />
      </Modal>
    </main>
  );
}
